import React, { CSSProperties, ReactElement } from 'react';

import uuid from 'uuid';

import { Deck, DeckAPI, DeckGroupBackgroundColor, DeckProps, DeckState, DeckValue, Form } from '@philips-emr/tws-components';
import DeckContext from '@philips-emr/tws-components/lib/components/deck/deck-context';
import DeckGroup from '@philips-emr/tws-components/lib/components/deck/deck-group/deck-group';
import { Density, LayoutGap, SelectionType, ThemeTonalRange } from '@philips-emr/tws-constants';
import { AwesomeComponent, ComponentType, ContainerProvider, Oswald } from '@philips-emr/tws-core';
import { bool, eventListener, formatters, oneOf, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { RenderEvent, SelectEvent } from '@philips-emr/tws-core/lib/component/types';
import { SelectItemEvent } from '@philips-emr/tws-core/lib/component/types/selectable-list';
import { ModalService } from '@philips-emr/tws-core/lib/services';
import { ModalProps } from '@philips-emr/tws-core/lib/services/modal-service';
import { enumValues, getBreakpoint, getSlot, isEmptyObject, isFunction } from '@philips-emr/tws-utils';

import './deck.css';
import BaseCardList from './base-card-list';

const acceptedTypes = [
  ComponentType.BEHAVIOR,
  ComponentType.DATA_DISPLAY,
  ComponentType.DATA_ENTRY,
  ComponentType.FORM,
  ComponentType.LIST,
];

@AwesomeComponent({ api: DeckAPI, type: ComponentType.LIST })
export class HpmsCustomDeck extends BaseCardList<DeckContext, DeckProps, DeckState> {
  static propTypes = {
    ...BaseCardList.propTypes,
    collapsible: bool.group('group'),
    formatters: formatters,
    groupBackgroundColor: oneOf(enumValues(DeckGroupBackgroundColor)).group('group'),
    groupBy: string.group('group'),
    groupContentMargin: string.group('group'),
    groupContentPadding: string.group('group'),
    groupGap: oneOf(enumValues(LayoutGap)).group('group'),
    initialCollapsed: bool.group('group'),
    onClick: eventListener,
    onDynamicContentRender: eventListener,
    onSelectGroup: eventListener,
    onUnselectGroup: eventListener,
  };

  static defaultProps = {
    ...BaseCardList.defaultProps,
    collapsible: false,
    groupBackgroundColor: DeckGroupBackgroundColor.DEFAULT,
    groupGap: LayoutGap.NONE,
    initialCollapsed: false,
  };

  state: DeckState = {
    ...this.state,
    selectedGroups: [],
  };

  setContext(): void {
    this.setState({ context: new DeckContext() });
  }

  getGroupedData(): Record<string, DeckValue[]> | null {
    const { data, groupBy } = this.props;

    if (!data || !Array.isArray(data) || !groupBy) {
      return null;
    }

    const groupedData = data.reduce<Record<string, DeckValue[]>>((accumulator, value) => {
      const groupByFieldValue = String(value[groupBy]);

      if (!groupByFieldValue) {
        return accumulator;
      }

      const oldGroupValue = accumulator[groupByFieldValue] || [];
      oldGroupValue.push(value);
      accumulator[groupByFieldValue] = oldGroupValue;

      return accumulator;
    }, {});

    if (isEmptyObject(groupedData)) {
      return null;
    }

    return groupedData;
  }

  isGroupSelected(groupKey: string): boolean {
    const { selectedGroups } = this.state;

    if (!this.isGroupSelectable()) {
      return false;
    }

    return this.getContext().isGroupSelected(groupKey, selectedGroups);
  }

  getFilteredSelectedGroups(groupKey: string, select: boolean): string[] {
    const { selectionType = Deck.defaultProps.selectionType } = this.props;
    const { selectedGroups } = this.state;

    return this.getContext().modifyGroupSelection({
      item: groupKey,
      remove: !select,
      selection: selectedGroups,
      type: selectionType,
    });
  }

  getFilteredSelectedCards(cards: DeckValue[], select: boolean): string[] {
    const { selectionType } = this.props;
    const { selectedCards } = this.state;

    const filteredSelectedCards = selectedCards.filter(key => !cards.some(card => this.getCardKey(card) === key));

    if (!select) {
      return filteredSelectedCards;
    }

    const cardsKeys = cards.map(card => this.getCardKey(card));

    return selectionType === SelectionType.SINGLE_GROUP ? cardsKeys : [...filteredSelectedCards, ...cardsKeys];
  }

  updateGroupSelection(groupKey: string, cards: DeckValue[], selected: boolean): void {
    const {
      onSelectGroup,
      onSelectItem,
      onUnselectGroup,
      onUnselectItem,
      selectionType = Deck.defaultProps.selectionType,
    } = this.props;

    if (!this.isGroupSelectable()) {
      return;
    }

    const newSelectedGroups = this.getFilteredSelectedGroups(groupKey, selected);
    const newSelectedCards = this.getFilteredSelectedCards(cards, selected);

    const callback = (): void => {
      const groupEvent = selected ? onSelectGroup : onUnselectGroup;
      const itemEvent = selected ? onSelectItem : onUnselectItem;

      if (this.getContext().allowGroupSelectionEvent(selectionType)) {
        if (isFunction(groupEvent)) {
          groupEvent(new SelectItemEvent<DeckValue[]>({ item: cards }));
        }
        return;
      }

      if (selectionType === SelectionType.MULTIPLE && isFunction(itemEvent)) {
        cards.forEach(card => {
          itemEvent(new SelectItemEvent<DeckValue>({ item: card }));
        });
      }
    };

    this.setState({ selectedCards: newSelectedCards, selectedGroups: newSelectedGroups }, callback);
  }

  onBeforeUpdateCardSelection(event: SelectEvent<any>, card: DeckValue): void {
    const { groupBy, selectionType } = this.props;

    if (event.selected || SelectionType.MULTIPLE !== selectionType) {
      return;
    }

    this.setState({ selectedGroups: this.getFilteredSelectedGroups(String(card[groupBy]), false) });
  }

  renderGroupHeaderContent(groupKey: string, cards: DeckValue[]): ReactElement | null {
    const { onDynamicContentRender } = this.props;
    const data = cards[0];
    let renderFn = (): ReactElement | null => {

      return (
        <div className="c-deck__group-header-default-content">
          {this.renderHeader(data)}
        </div>
      );
    };

    if (isFunction(onDynamicContentRender)) {
      const render = (fn: () => ReactElement): void => {
        renderFn = fn;
      };

      onDynamicContentRender(
        new RenderEvent({
          data: cards,
          entry: groupKey,
          render,
          type: 'grouped-deck-header-content',
        })
      );
    }

    return renderFn();
  }

  isGroupSelectable(): boolean {
    const { selectable, selectionType = Deck.defaultProps.selectionType } = this.props;

    return this.getContext().allowGroupSelection(selectable, selectionType);
  }

  sendDocument(nrSeqProvider: number, nrSeqDocumentType: number, dsDocument: string): void {
    const modalWithFragment: ModalProps = {
      id: 'send-documents-modal',
      closeable: true,
      fragment: 'send-documents',
      title: 'provider.send-documents',
      theme: ThemeTonalRange.ULTRA_LIGHT,
      contextData: { nrSeqProvider, nrSeqDocumentType, dsDocument },
      focusOnOpen: true,
    };

    const breakpoints = getBreakpoint() as string;
    if (breakpoints == 'sm' || breakpoints == 'xsm') {
      ModalService.openFullscreen(modalWithFragment);
    } else {
      ModalService.openDialog(modalWithFragment);
    }
  }

  renderCardGroups(data: Record<string, DeckValue[]>): ReactElement[] {
    const {
      collapsible,
      groupBackgroundColor,
      groupContentMargin,
      groupContentPadding,
      initialCollapsed,
      selectionType = Deck.defaultProps.selectionType,
    } = this.props;

    return Object.entries(data).map(([groupKey, cards]) => {
      const Container = ContainerProvider.provide();

      const groupProps = {
        collapsible,
        context: this.getContext(),
        groupBackgroundColor,
        groupContentMargin,
        groupContentPadding,
        groupKey,
        initialCollapsed,
        onSelect: (selected: boolean) => this.updateGroupSelection(groupKey, cards, selected),
        renderHeaderContent: () => this.renderGroupHeaderContent(groupKey, cards),
        selectable: this.isGroupSelectable(),
        selected: this.isGroupSelected(groupKey),
        selectionType,
      };

      return (
        <div className="c-deck__group-grid-item" key={groupKey}>
          <Container slot="header" uuid={uuid()}>
            <DeckGroup {...groupProps} >
              <Container slot="content" uuid={uuid()}>
                {this.renderCards(cards)}
              </Container>
            </DeckGroup>
          </Container>
        </div>
      );
    });
  }

  renderContainer(): ReactElement | null {
    const { children, uuid } = this.props;
    const Container = ContainerProvider.provide();

    if (!Oswald.isDesigning()) {
      return null;
    }

    return (
      <Container acceptedTypes={acceptedTypes} className="u-grid-col-12" repeated uuid={uuid}>
        {children}
      </Container>
    );
  }

  renderHeader(data: {}): ReactElement[] | ReactElement | null {
    const children = getSlot(this.props.children, 'header');
    return (
      <Form density={Density.TOUCH} data={data} uuid={uuid()} editing={false} locked={true}>
        {children}
      </Form>
    );
  }

  renderContent(): ReactElement[] | ReactElement | null {
    const { accumulate, data, loading, groupBy, groupGap } = this.props;
    const Container = ContainerProvider.provide();

    if (Oswald.isDesigning()) {
      return this.renderContainer();
    }

    if (!data || !Array.isArray(data) || (!accumulate && loading)) {
      return null;
    }

    const groupsWrapperStyle: CSSProperties = {
      'gridRowGap': groupGap,
    };

    if (groupBy) {
      const groupedData = this.getGroupedData();

      return (
        <div className="c-deck__group-grid" style={groupsWrapperStyle}>
          {groupedData ? this.renderCardGroups(groupedData) : null}
        </div>

      );
    }

    return <div>
      <Container slot="content" uuid={uuid()}>
        {this.renderCards(data)}
      </Container>
    </div>;
  }

  render(): ReactElement | null {
    const { context } = this.state;

    if (!context) {
      return null;
    }

    return (
      <DeckContext.Context.Provider value={context} >
        <div className="c-deck">
          {this.renderNotFound()}
          {this.renderContent()}
          {this.renderLoading()}
          {this.renderLoadMore()}
        </div>
      </DeckContext.Context.Provider>
    );
  }
}

export default HpmsCustomDeck;

